import React, { useState, useRef, useEffect } from "react"; 
import { Link } from "react-router-dom";
import Collapse from "react-bootstrap/Collapse";
import Swal from "sweetalert2";
import RepairServices from "../../../services/RepairServices";
import ApprovalOrRejectReqectionModal from "./ApprovalOrRejectionModal";

const PendingApprovalAdmin = () => {
  const user = JSON.parse(localStorage.getItem("userDetails"));

  // ---------------------------------------------------
  // MODAL
  // ---------------------------------------------------
  const [showModal, setShowModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);

  // ---------------------------------------------------
  // STORE LOGIC
  // ---------------------------------------------------
  const stores = user?.data?.user?.store || [];
  const defaultStore = stores[0]?._id || "";

  const [selectedStore, setSelectedStore] = useState(defaultStore);

  // ---------------------------------------------------
  // REPAIR & ITEMS
  // ---------------------------------------------------
  const [repairs, setRepairs] = useState([]);
  const [selectedRepair, setSelectedRepair] = useState("");
  const [items, setItems] = useState([]);

  const [run, setRun] = useState(0);
  const [loading, setLoading] = useState(false);

  // UI
  const [open, setOpen] = useState(true);
  const [open2, setOpen2] = useState(true);

  // ---------------------------------------------------
  // PAGINATION SETTINGS
  // ---------------------------------------------------
  const sort = 50;
  const activePag = useRef(0);

  // ❗ FIX: dummy replaced with meaningful force-render trigger
  const [pageTrigger, setPageTrigger] = useState(0);

  const pagination = Math.ceil(items.length / sort);

  const goToPage = (i) => {
    activePag.current = i;
    setPageTrigger(i); // ensures UI updates
  };

  useEffect(() => {
    activePag.current = 0;
  }, [items]);

  // ---------------------------------------------------
  // GLOBAL API ERROR HANDLER
  // ---------------------------------------------------
  const handleApiError = (err) => {
    Swal.fire({
      icon: "error",
      title: "Error",
      text:
        err?.response?.data?.message ||
        err?.message ||
        "Something went wrong while fetching data.",
    });
  };

  // ---------------------------------------------------
  // FETCH REPAIR ORDERS (status: submit)
  // ---------------------------------------------------
  useEffect(() => {
    if (!selectedStore) return;

    setLoading(true);

    RepairServices.getRequest(selectedStore, "submit")
      .then((res) => {
        const arr = res?.data?.data || [];
        setRepairs(arr);
        setSelectedRepair(arr.length > 0 ? arr[0]._id : "");
      })
      .catch(handleApiError)
      .finally(() => setLoading(false));
  }, [selectedStore]);

  // ---------------------------------------------------
  // FETCH ITEMS FOR SELECTED REPAIR (status: pending)
  // ---------------------------------------------------
  useEffect(() => {
    if (!selectedStore || !selectedRepair) return;

    setLoading(true);

    RepairServices.getAllItem(selectedRepair, "pending", selectedStore)
      .then((res) => setItems(res?.data?.data || []))
      .catch(handleApiError)
      .finally(() => setLoading(false));
  }, [selectedStore, selectedRepair, run]);

  // ---------------------------------------------------
  // APPROVE OR REJECT
  // ---------------------------------------------------
  const handleApproval = (item, status) => {
    Swal.fire({
      title: `Confirm ${status}?`,
      text: `Repair: ${item?.repairOrder?.repairOrderNo} | Part: ${item?.repairOrder?.inventory?.masterItem?.partNo}`,
      icon: status === "approvedByAdmin" ? "success" : "warning",
      showCancelButton: true,
      input: status === "rejected" ? "textarea" : null,
      inputPlaceholder: "Enter remarks",
      inputValidator: (value) => {
        if (status === "rejected" && !value) return "Remarks required!";
      },
    }).then((result) => {
      if (!result.isConfirmed) return;

      const remark = status === "rejected" ? result.value : "approved";

      RepairServices.updateItem(item._id, {
        status,
        approveQty: item.qtyRequired,
        adminRemark: remark,
      })
        .then(() => {
          Swal.fire("Success", `Item ${status}`, "success");
          setRun((prev) => prev + 1);
        })
        .catch(handleApiError);
    });
  };

  // ---------------------------------------------------
  // RENDER UI
  // ---------------------------------------------------
  return (
    <>
      {/* MODAL */}
      {showModal && (
        <ApprovalOrRejectReqectionModal
          show={showModal}
          item={selectedItem}
          handleClose={() => setShowModal(false)}
          onSuccess={() => setRun((p) => p + 1)}
        />
      )}

      <div className="row">
        {loading ? (
          <div className="text-center my-4">
            <div className="spinner-border text-primary"></div>
            <p>Loading...</p>
          </div>
        ) : (
          <div className="col-xl-12">

            {/* FILTER SECTION */}
            <div className="filter cm-content-box box-primary">
              <div className="content-title">
                <div className="cpa">
                  <i className="fas fa-filter me-2"></i>Filter
                </div>
                <div className="tools">
                  <Link to="#" onClick={() => setOpen(!open)}>
                    <i className="fas fa-angle-up" />
                  </Link>
                </div>
              </div>

              <Collapse in={open}>
                <div className="cm-content-body form excerpt">
                  <div className="card-body">
                    <div className="row">
                      {/* Store */}
                      <div className="col-6">
                        <label>Select Store</label>
                        <select
                          className="form-select"
                          value={selectedStore}
                          onChange={(e) => setSelectedStore(e.target.value)}
                        >
                          {stores.map((s) => (
                            <option value={s._id} key={s._id}>
                              {s.name}
                            </option>
                          ))}
                        </select>
                      </div>

                      {/* Repairs */}
                      <div className="col-6">
                        <label>Select Repair Order</label>
                        <select
                          className="form-select"
                          value={selectedRepair}
                          onChange={(e) => setSelectedRepair(e.target.value)}
                        >
                          {repairs.length > 0 ? (
                            repairs.map((r) => (
                              <option key={r._id} value={r._id}>
                                {r.repairOrderNo}
                              </option>
                            ))
                          ) : (
                            <option>No repair found</option>
                          )}
                        </select>
                      </div>
                    </div>
                  </div>
                </div>
              </Collapse>
            </div>

            {/* TABLE SECTION */}
            <div className="filter cm-content-box box-primary mt-2">
              <div className="content-title">
                <div className="cpa">
                  <i className="fa fa-list me-2"></i>Pending Approval
                  <span className="badge bg-orange ms-2">{items.length}</span>
                </div>
                <div className="tools">
                  <Link to="#" onClick={() => setOpen2(!open2)}>
                    <i className="fas fa-angle-up" />
                  </Link>
                </div>
              </div>

              <Collapse in={open2}>
                <div className="cm-content-body form excerpt">

                  <div
                    style={{
                      overflowX: "auto",
                      overflowY: "auto",
                      maxHeight: "420px",
                      whiteSpace: "nowrap",
                    }}
                  >
                    <table
                      className="table table-bordered table-striped table-responsive-lg table-condensed"
                      id="transactions-data"
                    >
                      <thead
                        style={{
                          position: "sticky",
                          top: 0,
                          background: "#fff",
                          zIndex: 5,
                        }}
                      >
                        <tr>
                          <th>S.No</th>
                          <th>Repair No</th>
                          <th>Inv Part No</th>
                          <th>Inv Description</th>
                          <th>Repair Part No</th>
                          <th>Repair Description</th>
                          <th>Requested Qty</th>
                          <th>Edit</th>
                          <th>Approve / Reject</th>
                        </tr>
                      </thead>

                      <tbody>
                        {items.length > 0 ? (
                          items
                            .slice(
                              activePag.current * sort,
                              (activePag.current + 1) * sort
                            )
                            .map((item, idx) => {
                              const inv = item?.repairOrder?.inventory;
                              const master = item?.masterlist;

                              return (
                                <tr key={item._id}>
                                  <td>{activePag.current * sort + idx + 1}</td>
                                  <td>{item?.repairOrder?.repairOrderNo}</td>
                                  <td>{inv?.masterItem?.partNo}</td>
                                  <td className="scrollable-description">{inv?.masterItem?.description}</td>
                                  <td>{master?.partNo}</td>
                                  <td className="scrollable-description">{master?.description}</td>
                                  <td>{item.qtyRequired}</td>

                                  {/* Edit */}
                                  <td className="text-center">
                                    <button
                                      className="btn btn-primary btn-xs sharp"
                                      onClick={() => {
                                        setSelectedItem(item);
                                        setShowModal(true);
                                      }}
                                    >
                                      <i className="fa fa-pencil"></i>
                                    </button>
                                  </td>

                                  {/* Approve / Reject */}
                                  <td className="text-center">
                                    <button
                                      className="btn btn-success btn-xs me-2"
                                      onClick={() =>
                                        handleApproval(item, "approvedByAdmin")
                                      }
                                    >
                                      <i className="fa fa-thumbs-up"></i>
                                    </button>

                                    <button
                                      className="btn btn-danger btn-xs"
                                      onClick={() =>
                                        handleApproval(item, "rejected")
                                      }
                                    >
                                      <i className="fa fa-thumbs-down"></i>
                                    </button>
                                  </td>
                                </tr>
                              );
                            })
                        ) : (
                          <tr>
                            <td colSpan="12" className="text-center">
                              No items found
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>

                  {/* PAGINATION */}
                  <div className="d-sm-flex text-center justify-content-between align-items-center mt-3">

                    <div className="dataTables_info">
                      Showing {activePag.current * sort + 1} to{" "}
                      {Math.min(items.length, (activePag.current + 1) * sort)} of{" "}
                      {items.length} entries
                    </div>

                    <div className="dataTables_paginate paging_simple_numbers">
                      <Link
                        className="paginate_button previous"
                        to="#"
                        onClick={() =>
                          activePag.current > 0 &&
                          goToPage(activePag.current - 1)
                        }
                      >
                        <i className="fa fa-angle-double-left" />
                      </Link>

                      <span>
                        {Array.from({ length: pagination }, (_, i) => (
                          <Link
                            key={i}
                            className={`paginate_button ${
                              activePag.current === i ? "current" : ""
                            }`}
                            to="#"
                            onClick={() => goToPage(i)}
                          >
                            {i + 1}
                          </Link>
                        ))}
                      </span>

                      <Link
                        className="paginate_button next"
                        to="#"
                        onClick={() =>
                          activePag.current + 1 < pagination &&
                          goToPage(activePag.current + 1)
                        }
                      >
                        <i className="fa fa-angle-double-right" />
                      </Link>
                    </div>

                  </div>

                </div>
              </Collapse>
            </div>

          </div>
        )}
      </div>
    </>
  );
};

export default PendingApprovalAdmin;
